import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

// ── Types ───────────────────────────────────────────────

export interface SalesModule {
  id: string
  name: string
  slug: string
  pages: string[]
  description: string | null
  price: number
  active: boolean
  createdAt: number
}

export interface SalesSubscription {
  id: string
  clientUserId: string
  clientName: string
  clientUsername: string
  status: 'active' | 'overdue' | 'cancelled' | 'trial'
  basePrice: number
  totalPrice: number
  moduleIds: string[]
  moduleNames: string[]
  abacatepayCustomerId: string | null
  nextBillingDate: string | null
  blockedAt: number | null
  notes: string | null
  createdAt: number
}

export interface SalesBilling {
  id: string
  subscriptionId: string
  clientName: string
  amount: number
  status: 'pending' | 'paid' | 'expired' | 'cancelled'
  billingUrl: string | null
  dueDate: string | null
  paidAt: number | null
  createdAt: number
}

export interface SalesDashboard {
  mrr: number
  totalClients: number
  activeClients: number
  overdueClients: number
  receivedThisMonth: number
}

export interface SalesClient {
  id: string
  name: string
  username: string
}

// ── Hooks ───────────────────────────────────────────────

export function useSalesDashboard() {
  return useQuery<SalesDashboard>({
    queryKey: ['sales-dashboard'],
    queryFn: () => api.get('/sales/dashboard'),
  })
}

export function useSalesModules() {
  return useQuery<SalesModule[]>({
    queryKey: ['sales-modules'],
    queryFn: () => api.get('/sales/modules'),
  })
}

export function useCreateModule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; pages: string[]; price?: number; description?: string }) =>
      api.post<{ ok: boolean; module: SalesModule }>('/sales/modules', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-modules'] }),
  })
}

export function useUpdateModule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; pages?: string[]; price?: number; description?: string; active?: boolean }) =>
      api.put<{ ok: boolean }>(`/sales/modules/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-modules'] })
      qc.invalidateQueries({ queryKey: ['sales-subscriptions'] })
    },
  })
}

export function useDeleteModule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/sales/modules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-modules'] }),
  })
}

export function useSalesSubscriptions() {
  return useQuery<SalesSubscription[]>({
    queryKey: ['sales-subscriptions'],
    queryFn: () => api.get('/sales/subscriptions'),
  })
}

export function useCreateSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { clientUserId: string; moduleIds: string[]; basePrice?: number; notes?: string }) =>
      api.post<{ ok: boolean; subscription: { id: string; totalPrice: number } }>('/sales/subscriptions', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-subscriptions'] })
      qc.invalidateQueries({ queryKey: ['sales-dashboard'] })
    },
  })
}

export function useUpdateSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; moduleIds?: string[]; basePrice?: number; status?: string; notes?: string }) =>
      api.put<{ ok: boolean; totalPrice: number }>(`/sales/subscriptions/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-subscriptions'] })
      qc.invalidateQueries({ queryKey: ['sales-dashboard'] })
    },
  })
}

export function useDeleteSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/sales/subscriptions/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-subscriptions'] })
      qc.invalidateQueries({ queryKey: ['sales-dashboard'] })
    },
  })
}

export function useGenerateBilling() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (subscriptionId: string) =>
      api.post<{ ok: boolean; billing: { id: string; billingUrl: string; amount: number; dueDate: string } }>(`/sales/subscriptions/${subscriptionId}/generate-billing`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-billings'] })
      qc.invalidateQueries({ queryKey: ['sales-dashboard'] })
    },
  })
}

export function useSalesBillings(status?: string) {
  return useQuery<SalesBilling[]>({
    queryKey: ['sales-billings', status],
    queryFn: () => api.get('/sales/billings' + (status ? `?status=${status}` : '')),
  })
}

export function useSalesClients() {
  return useQuery<SalesClient[]>({
    queryKey: ['sales-clients'],
    queryFn: () => api.get('/sales/clients'),
  })
}
